import type { AnyValue } from "@/lib/rowTypes";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useSfx, type SfxName } from "@/hooks/useSfx";
import { useWallet } from "@/hooks/useWallet";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { AlertTriangle, CheckCircle2, XCircle, ArrowRight, Sparkles, DollarSign } from "lucide-react";
import {
  shuffleLifeEventOptions,
  markLifeEventSeen,
  type LaunchLifeEvent,
  type LaunchLifeEventOption,
} from "@/data/launchLifeEvents";
import { LumiCoachingCard } from "@/components/lumi/LumiCoachingCard";
import { getLumiLifeEventCoaching } from "@/data/lumiLaunchCoaching";

type Phase = "intro" | "choosing" | "locked" | "result";

interface Props {
  event: LaunchLifeEvent;
  onContinue: () => void;
}

/**
 * Beta-only Life Event overlay. Renders a real-world interruption between
 * beta path items. Options are shuffled per attempt; correctness is graded
 * against `event.correctOptionId`, never against option position.
 */
export default function LaunchLifeEventOverlay({ event, onContinue }: Props) {
  const { playSfx } = useSfx();
  const { addFunds, refetch } = useWallet();
  const { user } = useAuth();

  const [phase, setPhase] = useState<Phase>("intro");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  const shuffled = useMemo<LaunchLifeEventOption[]>(() => shuffleLifeEventOptions(event), [event.id]);
  const appearedRef = useRef(false);

  useEffect(() => {
    if (appearedRef.current) return;
    appearedRef.current = true;
    playSfx(event.sfx.appear as SfxName);
  }, [event.sfx.appear, playSfx]);

  const isCorrect = selectedId === event.correctOptionId;
  const impact = isCorrect ? event.walletImpactCorrect : event.walletImpactIncorrect;

  const handleSelect = (id: string) => {
    if (phase !== "intro" && phase !== "choosing") return;
    setSelectedId(id);
    setPhase("choosing");
    playSfx(event.sfx.select as SfxName);
  };

  const handleLock = async () => {
    if (!selectedId) return;
    playSfx(event.sfx.confirm as SfxName);
    setPhase("locked");

    const correct = selectedId === event.correctOptionId;
    const delta = correct ? event.walletImpactCorrect : event.walletImpactIncorrect;

    // Result SFX
    setTimeout(() => {
      playSfx((correct ? event.sfx.correct : event.sfx.incorrect) as SfxName);
      setTimeout(() => {
        if (delta !== 0) {
          playSfx((delta > 0 ? event.sfx.moneyGain : event.sfx.moneyLoss) as SfxName);
        }
      }, 250);
      setPhase("result");
    }, 350);

    // Wallet impact (server-validated). Best-effort: log either way.
    try {
      if (delta !== 0) {
        await addFunds(
          delta,
          `Life Event: ${event.title}`,
          undefined,
          delta > 0 ? "event_bonus" : "life_event_penalty",
        );
        await refetch();
      }
    } catch {
      /* wallet failure shouldn't block UX */
    }

    // Audit ledger (best-effort; ignore failures)
    try {
      if (user) {
        await (supabase).from("learning_event_ledger").insert({
          user_id: user.id,
          event_type: "beta_life_event",
          event_payload: {
            eventId: event.id,
            choice: selectedId,
            correct,
            walletDelta: delta,
          },
        } as AnyValue);
      }
    } catch {
      /* best-effort */
    }

    markLifeEventSeen(event.id);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-xl p-4 animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="life-event-title"
    >
      <div className="glass-card glow-border max-w-2xl w-full p-6 sm:p-8 space-y-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-accent/15 border border-accent/40 flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-accent" />
          </div>
          <div className="text-xs uppercase tracking-widest text-accent font-heading">
            Real-World Moment
          </div>
        </div>

        <h2 id="life-event-title" className="font-heading font-bold text-2xl text-foreground holo-text">
          {event.title}
        </h2>

        <p className="text-sm sm:text-base text-foreground/90 leading-relaxed">
          {event.scenario}
        </p>

        {phase !== "result" && (
          <>
            <p className="font-heading font-semibold text-foreground">{event.question}</p>
            <div className="grid gap-2">
              {shuffled.map((opt, idx) => {
                const letter = String.fromCharCode(65 + idx);
                const active = selectedId === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => handleSelect(opt.id)}
                    disabled={phase === "locked"}
                    className={[
                      "w-full text-left rounded-xl border px-4 py-3 transition-all",
                      "bg-background/40 hover:bg-accent/10",
                      active
                        ? "border-accent shadow-[0_0_18px_hsl(var(--accent)/0.45)] bg-accent/10"
                        : "border-border/60",
                    ].join(" ")}
                  >
                    <span className="font-heading text-accent mr-2">{letter}.</span>
                    <span className="text-foreground">{opt.text}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleLock}
                disabled={!selectedId || phase === "locked"}
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-heading font-semibold gap-2"
              >
                Lock in choice
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}

        {phase === "result" && (
          <div className="space-y-4">
            <div
              className={[
                "rounded-xl border p-4 flex items-start gap-3",
                isCorrect
                  ? "border-emerald-500/50 bg-emerald-500/10"
                  : "border-red-500/50 bg-red-500/10",
              ].join(" ")}
            >
              {isCorrect ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
              )}
              <div className="space-y-1">
                <div className="font-heading font-semibold text-foreground">
                  {isCorrect ? "Strong call." : "Tough lesson."}
                </div>
                <div className="text-sm text-foreground/90">{event.explanation}</div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-border/60 bg-background/40 p-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
                  <DollarSign className="w-3 h-3" /> Money impact
                </div>
                <div
                  className={[
                    "font-heading font-bold text-xl",
                    impact > 0 ? "text-emerald-400" : impact < 0 ? "text-red-400" : "text-foreground",
                  ].join(" ")}
                >
                  {impact > 0 ? `+$${impact}` : impact < 0 ? `-$${Math.abs(impact)}` : "$0"}
                </div>
              </div>
              <div className="rounded-xl border border-border/60 bg-background/40 p-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Skill gained
                </div>
                <div className="font-heading font-semibold text-foreground">{event.skillGained}</div>
              </div>
            </div>

            <div className="rounded-xl border border-border/60 bg-background/40 p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                Why it mattered
              </div>
              <div className="text-sm text-foreground/90">{event.whyItMattered}</div>
            </div>

            {(() => {
              const coaching = getLumiLifeEventCoaching(event.id, isCorrect);
              return coaching ? (
                <LumiCoachingCard line={coaching} variant="inline" contextId={event.id} />
              ) : null;
            })()}

            <Button
              onClick={onContinue}
              className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-heading font-semibold gap-2"
            >
              Continue beta path
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}